import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Card,
    Button,
    Table,
    Icon,
    InfoText,
    Status,
    FilterSetController,
    Filter,
    EmptyState,
    Paginator,
    ToastContext,
    Toggle,
    TableCellDropdown,
    Dropdown,
    Tooltip,
    Modal
} from '@weezevent/nacre';
import LoadingCard from '../../../../components/loadingCard';
import CreateProductModal from './createProductModal';
import Style from './productsTable.module.css';
import moment from 'moment-timezone';
import VariantModel from '../../../../models/variantModel';
import ProductModel from '../../../../models/productModel';
import VariantModal from '../../detail/variants/components/variantModal';
import { useForm, requiredField, fetcher } from '@weezevent/weezjs-core';

function ProductStatus({ productIsArchived, productIsDisabled }) {
    const { t } = useTranslation();

    const statusMapping = {
        enabled: { color: 'green', text: t('ticket.cross-selling.visible') },
        disabled: { color: 'orange', text: t('ticket.cross-selling.invisible') },
        archived: { color: 'red', text: t('ticket.common.archived') }
    };
    const status = React.useMemo(() => {
        let value = productIsDisabled ? 'disabled' : 'enabled';
        if (productIsArchived) {
            value = 'archived';
        }
        return statusMapping[value];
    }, [productIsArchived, productIsDisabled]);

    return <Status text={status.text} color={status.color} />;
}

function VariantRow({ variant, product, organization, variantsStatus, setVariantsStatus, setContextEditVariantModal, setContextDeleteModal, setFormObject }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const variantIsDisabled = React.useMemo(() => variantsStatus[variant.id], [variantsStatus, variant.id]);

    const handleToggleActivation = React.useCallback(() => {
        const newDisabledState = !variantIsDisabled;
        const previousDisabledState = variantIsDisabled;

        setVariantsStatus(prevStatus => ({
            ...prevStatus,
            [variant.id]: newDisabledState
        }));

        const updatedVariant = new VariantModel({
            id: variant.id,
            disabled: newDisabledState
        });

        updatedVariant
            .save({
                organizationId: organization.id,
                productId: product.id,
                id: variant.id
            })
            .then(() => {
                toast.success(t('ticket.cross-selling.listVariant.variantEdited'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.cross-selling.listVariant.variantEditError'));
                // If the request fail, then we change disabled value back to it's previous state
                setVariantsStatus(prevStatus => ({
                    ...prevStatus,
                    [variant.id]: previousDisabledState
                }));
            });
    }, [variant, variantIsDisabled, organization.id, product, setVariantsStatus, t, toast]);

    return (
        <Table.Tr>
            <Table.Td>
                <InfoText className={Style['name-cell-size']}>{variant.name}</InfoText>
            </Table.Td>
            <Table.Td className={Style['center-text']}>{variant.transaction_total_count}</Table.Td>
            <Table.Td>{variant.quota_total}</Table.Td>
            <Table.Td>{variant.price}</Table.Td>
            <Table.Td>
                <Toggle checked={!variantIsDisabled} onChange={() => handleToggleActivation()} />
            </Table.Td>
            <Table.Td>{/* Status for products only */}</Table.Td>
            <Table.Td className={Style['actions-block']}>
                {/* We add the icon here too so that the dropdown is aligne with product */}
                <Icon name={'edit'} className={Style['not-display-icon-edit']} onClick={() => {}} disabled={true} />
                <TableCellDropdown>
                    <Dropdown.Item
                        item={t('ticket.common.action.edit')}
                        onClick={() => {
                            setFormObject(
                                {
                                    quota: variant.quota_total,
                                    name: variant.name,
                                    id: variant.id,
                                    price: variant.price,
                                    ordering: variant.ordering,
                                    disabled: variant.disabled,
                                    reference: variant.reference,
                                    translations: {
                                        ...(variant.translations || {
                                            name: {}
                                        })
                                    }
                                },
                                true
                            );
                            setContextEditVariantModal(product);
                        }}
                    />
                    <Dropdown.Item
                        deleteItem
                        disabled={variant.transaction_total_count > 0 || product.archived_at}
                        item={t('ticket.common.action.delete')}
                        onClick={() => {
                            setContextDeleteModal(variant);
                        }}
                    />
                </TableCellDropdown>
            </Table.Td>
        </Table.Tr>
    );
}

function ProductRowDropDown({ organization, product, setContextModal, sync, setContextEditVariantModal, setContextDeleteModal, setFormObject }) {
    const history = useHistory();
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [productIsDisabled, setProductIsDisabled] = React.useState(product.disabled);

    const getInitialVariantsStatus = () => {
        if (!product || !product.variants) {
            return {};
        }

        return product.variants.reduce((acc, variant) => {
            acc[variant.id] = variant.disabled;
            return acc;
        }, {});
    };
    const [variantsStatus, setVariantsStatus] = React.useState(getInitialVariantsStatus);

    const onArchive = React.useCallback(() => {
        product.archived_at = moment.utc().format('YYYY-MM-DDTHH:mm:ss');
        product
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.cross-selling.archive.success'));
                sync();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.cross-selling.archive.error'));
            });
    }, [organization, product, toast, sync]);

    const handleToggleActivation = React.useCallback(() => {
        const newDisabledState = !productIsDisabled;
        const previousDisabledState = productIsDisabled;
        setProductIsDisabled(newDisabledState);

        const updatedProduct = new ProductModel({
            id: product.id,
            disabled: newDisabledState
        });

        updatedProduct
            .save({
                organizationId: organization.id,
                productId: product.id
            })
            .then(() => {
                toast.success(t('ticket.cross-selling.add.productEdited'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.cross-selling.add.productEditError'));
                // If the request fail, then we change disabled value back to it's previous state
                setProductIsDisabled(previousDisabledState);
            });
    }, [productIsDisabled, organization.id, product, t, toast]);

    // When we patch the variants, the back automatically put the product to disabled if there is no more
    // variants enableb, since we're not reloading we also need to keep the behavior accurate on this case
    const hasVariantDisabled = React.useMemo(() => {
        return !Object.values(variantsStatus).some(isDisabled => isDisabled === false);
    }, [variantsStatus]);

    React.useEffect(() => {
        if (hasVariantDisabled && !productIsDisabled) {
            setProductIsDisabled(true);
        }
    }, [hasVariantDisabled, productIsDisabled]);

    const getCategoryIcon = () => {
        switch (product.category) {
            case 1:
                return 'credit-card';
            case 2:
                return 'shirt';
            case 3:
                return 'fee';
            default:
                return 'shirt';
        }
    };

    return (
        <>
            <Table.Tr className={Style['product-row']} onClick={() => setOpen(prev => !prev)}>
                <Table.Td className={Style['product-name-cell']}>
                    <Icon name={'chevron-right'} className={open ? Style['rotate-icon'] : ''} />
                    <div className={Style['icon-name-background']}>
                        <Icon className={Style['no-margin']} name={getCategoryIcon()} size={'huge'} />
                    </div>
                    <InfoText
                        className={Style['name-cell-size']}
                        subtitle={product.for_buyer ? t('ticket.cross-selling.add.radioForBuyer.for_buyer') : t('ticket.cross-selling.add.radioForBuyer.for_order')}
                    >
                        {product.name}
                    </InfoText>
                </Table.Td>
                <Table.Td className={Style['center-text']}>{product.transaction_total_count}</Table.Td>
                <Table.Td /> {/* Quota for variants only */}
                <Table.Td /> {/* Price for variants only */}
                <Table.Td>
                    {/* If no variants is enabled for a product, a patch to enable it will do nothing in back */}
                    <Tooltip margin={false} triggerOnHover={hasVariantDisabled} visible={hasVariantDisabled} title={t('ticket.cross-selling.visibility_tooltip')}>
                        <Toggle checked={!productIsDisabled} onChange={() => handleToggleActivation()} disabled={hasVariantDisabled || product.archived_at} />
                    </Tooltip>
                </Table.Td>
                <Table.Td>
                    <ProductStatus productIsArchived={product.archived_at} productIsDisabled={productIsDisabled} />
                </Table.Td>
                <Table.Td className={Style['actions-block']}>
                    <Icon
                        name={'edit'}
                        onClick={e => {
                            history.push(`/ticket/O${organization.id}/products/${product.id}/settings`);
                        }}
                    />
                    <TableCellDropdown>
                        <Dropdown.Item
                            disabled={product.archived_at}
                            onClick={() => {
                                onArchive();
                            }}
                            item={t('ticket.common.action.archive')}
                        />
                        <Dropdown.Item
                            item={t('ticket.common.action.duplicate')}
                            onClick={() => {
                                setContextModal(product);
                            }}
                        />
                    </TableCellDropdown>
                </Table.Td>
            </Table.Tr>
            {open &&
                product.variants?.map((variant, index) => (
                    <VariantRow
                        key={variant.id}
                        variant={variant}
                        product={product}
                        organization={organization}
                        variantsStatus={variantsStatus}
                        setVariantsStatus={setVariantsStatus}
                        setContextEditVariantModal={setContextEditVariantModal}
                        setContextDeleteModal={setContextDeleteModal}
                        setFormObject={setFormObject}
                    />
                ))}
        </>
    );
}

export default function ProductsTable({ organization, products, loading, sync, filters, setFilters, currentPage, setCurrentPage, pageSize, totalCount }) {
    const { t } = useTranslation();
    const [contextModal, setContextModal] = React.useState(null);
    const [contextVariantEditModal, setContextEditVariantModal] = React.useState(null);
    const [contextDeleteModal, setContextDeleteModal] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const optionsVisibility = [
        {
            key: 'disabled',
            text: t('ticket.cross-selling.visible'),
            name: 'False'
        },
        {
            key: 'disabled',
            text: t('ticket.cross-selling.notVisible'),
            name: 'True'
        },
        {
            key: 'disabled',
            text: t('ticket.common.archived'),
            name: 'Archived'
        }
    ];

    const optionsTarget = [
        {
            key: 'categoryAttendee',
            text: t('ticket.cross-selling.attendee'),
            name: 'False'
        },
        {
            key: 'categoryBuyer',
            text: t('ticket.cross-selling.buyer'),
            name: 'True'
        }
    ];

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    // It's a hack, we have a problem concerning quota sync on our variants, so if the quota is edited, we wan't to call quotasProduct to force a sync and have the good one.
    const updateQuota = React.useCallback(() => {
        return fetcher.get(`/ticket/organizations/${organization.id}/quota_products`);
    }, [organization]);

    const defaultVariant = {
        quota: null,
        name: '',
        id: null,
        price: 0,
        ordering: 0,
        disabled: false,
        reference: '',
        translations: {
            name: {}
        }
    };

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(defaultVariant, {
        name: requiredField,
        price: requiredField
    });

    const resetModal = React.useCallback(() => {
        let _defaultVariant = { ...defaultVariant };

        setFormObject({
            ..._defaultVariant
        });

        // reset form errors
        Object.keys(formErrors).forEach(key => {
            delete formErrors[key];
        });
    }, [setFormObject]);

    const handleSave = React.useCallback(
        (value, options = {}) => {
            const { id = null } = options;

            if (!formValidate()) {
                return toast.error(t('ticket.cross-selling.add.form_error'));
            }
            setSubmitting(true);

            const payload = id ? { id, ...value } : value;
            return new VariantModel(payload)
                .save({
                    organizationId: organization.id,
                    productId: contextVariantEditModal.id,
                    ...(id ? { id } : {})
                })
                .then(() => {
                    return updateQuota();
                })
                .then(() => {
                    toast.success(id ? t('ticket.cross-selling.listVariant.variantEdited') : t('ticket.cross-selling.listVariant.variantAdded'));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(id ? t('ticket.cross-selling.listVariant.variantEditError') : t('ticket.cross-selling.listVariant.variantSaveError'));
                })
                .finally(() => {
                    resetModal();
                    setSubmitting(false);
                    setContextEditVariantModal(null);
                });
        },
        [organization, contextVariantEditModal, resetModal, sync, updateQuota]
    );

    const onDeleteVariant = React.useCallback(() => {
        const variantToDelete = contextDeleteModal;

        const parentProduct = products.find(product => product.variants?.some(variant => variant.id === variantToDelete.id));

        setSubmitting(true);
        fetcher
            .delete(`/ticket/organizations/${organization.id}/products/${parentProduct.id}/variants/${variantToDelete.id}`)
            .then(() => {
                toast.success(t('ticket.cross-selling.listVariant.variantRemoved'));
            })
            .catch(e => {
                toast.error(t('ticket.cross-selling.listVariant.variantRemovedError'));
            })
            .finally(() => {
                sync();
                setSubmitting(false);
                setContextDeleteModal(null);
            });
    }, [contextDeleteModal, sync, organization, toast, t, products]);

    return (
        <>
            {/* create modal */}
            {contextModal && <CreateProductModal organization={organization} openModal={contextModal} setOpenModal={setContextModal} sync={sync} />}

            {/* edit modal */}
            {contextVariantEditModal && !contextVariantEditModal.archived_at && (
                <VariantModal
                    open={Boolean(contextVariantEditModal)}
                    setOpenModal={setContextEditVariantModal}
                    product={contextVariantEditModal}
                    quotas={contextVariantEditModal.quota_total}
                    form={form}
                    setForm={setForm}
                    setFormObject={setFormObject}
                    formErrors={formErrors}
                    resetModal={resetModal}
                    handleSave={handleSave}
                    loading={submitting || loading}
                />
            )}

            {/* delete modal */}
            <Modal
                size={'medium'}
                open={contextDeleteModal}
                allowExternalClosing={false}
                onClose={() => setContextDeleteModal(null)}
                title={t('ticket.cross-selling.listVariant.confirmDeleteVariant')}
                submitButton={<Button primary onClick={() => onDeleteVariant()} label={t('ticket.common.action.confirm')} />}
                cancelButton={<Button onClick={() => setContextDeleteModal(null)} label={t('ticket.common.action.cancel')} />}
            />

            <Card>
                <Card.Content>
                    <FilterSetController
                        total={products?.length}
                        isLoadingTotal={loading}
                        labelTotal={t('ticket.cross-selling.labelTotal', { count: products?.length })}
                        labelReset={t('ticket.common.action.reset')}
                        filterLabel={t('ticket.cross-selling.lebelFilters')}
                        initialValues={filters}
                        onChange={values => {
                            setFilters(values);
                        }}
                        search={{
                            onChange: (_, value) => {
                                onChangeSearch(value);
                            },
                            handleReset: (_, value) => {
                                onChangeSearch(null);
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                        onReset={() => {}}
                    >
                        <Filter name="disabled" labelName={t('ticket.common.status')} options={optionsVisibility} index="1" />
                        <Filter name="for_buyer" labelName={t('ticket.cross-selling.target')} options={optionsTarget} index="2" />
                    </FilterSetController>
                    <LoadingCard loading={loading}>
                        {products?.length > 0 ? (
                            <Table>
                                <Table.THead>
                                    <th>{t('ticket.cross-selling.listProducts.columns.cross-selling')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.sales')}</th>
                                    <th>{t('ticket.cross-selling.listVariant.quota')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.price')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.activation')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.status')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.actions')}</th>
                                </Table.THead>
                                <Table.TBody>
                                    {products?.map((product, index) => (
                                        <ProductRowDropDown
                                            key={product.id}
                                            organization={organization}
                                            product={product}
                                            setContextModal={setContextModal}
                                            setContextEditVariantModal={setContextEditVariantModal}
                                            setContextDeleteModal={setContextDeleteModal}
                                            setFormObject={setFormObject}
                                        />
                                    ))}
                                </Table.TBody>
                            </Table>
                        ) : (
                            <Card>
                                <Card.Content>
                                    <EmptyState
                                        icon={<Icon name="list" />}
                                        title={t('ticket.cross-selling.addObject')}
                                        subtitle={t('ticket.cross-selling.empty.sub')}
                                        labelButton={t('ticket.cross-selling.empty.labelButton')}
                                        onClick={() => setContextModal(true)}
                                    />
                                </Card.Content>
                            </Card>
                        )}
                    </LoadingCard>
                </Card.Content>
                <div className={Style['paginator-align']}>
                    <Paginator
                        currentPage={currentPage}
                        onPageChange={numberPage => {
                            setCurrentPage(numberPage);
                        }}
                        totalCount={totalCount !== '-' ? +totalCount : 0}
                        pageSize={pageSize}
                    />
                </div>
            </Card>
        </>
    );
}
